import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { jobAPI } from '../../../services/api';
import { messageFromError } from '../parts/errorMessage';
import { effectiveQuantity, lineTotal } from '../../../lib/quantity';

// Extracted verbatim from JobManagement.js (Task 5).
//
// `isLoading` stays owned by the orchestrator — this hook receives
// #52: this hook no longer takes `setIsLoading`. Opening the invoice overlay,
// downloading its PDF and saving it all run while that overlay is up, and the
// shared flag unmounts the table -- destroying the trigger focus returns to.
// `sendingInvoice` is genuinely separate state and lives here.
//
// `saveInvoiceToDatabase` used to call `loadDocuments()` directly (guarded by
// `activeTab === 'documents'`). That call is inverted into `onInvoiceSaved` so
// this action hook does not depend on `useDocuments` or on `activeTab`; the
// orchestrator supplies the guard.
//
// Coupling note (reported, not improvised around): three dependencies fall
// outside the shape the plan documented for this hook, all threaded in as
// constructor arguments rather than duplicated or reached across:
//   - `userPreferences` (owned by `useMechanicPreferences`) seeds the labor and
//     tax rates in `openInvoiceModal` and supplies the business block that
//     `sendInvoiceToCustomer`, `downloadInvoicePDF` and `saveInvoiceToDatabase`
//     attach to their payloads.
//   - `selectedJobForActions` / `setSelectedJobForActions` — the job every one
//     of these calls targets; orchestrator-owned because the actions dropdown
//     and the status flow read it too.
//   - `setShowInvoiceModal` — `openInvoiceModal` opens it.
//
// Deviation from the plan's listed interface (reported): `addPart`,
// `removePart` and `downloadInvoicePDF` moved in as well. The plan named only
// five functions, but these three touch nothing except this hook's own state
// plus the arguments above, and leaving them behind would split one flow across
// two files for no gain. Nothing about their behaviour changed.
const partsPayload = (parts) => parts.map((p) => ({
  part_name: p.part_name,
  part_number: p.part_number,
  quantity: effectiveQuantity(p.quantity),
  unit_cost: p.unit_cost,
  // '' is a line saved before part conditions existed: it stays unrecorded.
  condition: p.condition === '' ? null : p.condition || 'new',
}));

// The regulated fields as the API stores them. return_parts is tri-state in
// the form ('' = not asked) and true/false/null on the server.
const documentFields = (e) => ({
  vin: e.vin,
  license_plate: e.license_plate,
  odometer_in: e.odometer_in,
  work_description: e.work_description,
  return_parts: e.return_parts === 'yes' ? true : e.return_parts === 'no' ? false : null,
});

// The deposit as the API takes it. The percent only travels with 'percentage'.
const depositFields = (e) => {
  const type = e.deposit_type || 'none';
  return {
    deposit_type: type,
    deposit_percent: type === 'percentage' ? parseFloat(e.deposit_percent) || 0 : null,
  };
};

export const useInvoiceBuilder = ({
  onInvoiceSaved,
  userPreferences,
  selectedJobForActions,
  setSelectedJobForActions,
  setShowInvoiceModal
}) => {
  const [invoiceData, setInvoiceData] = useState(null);

  // Editable invoice fields state
  const [editableInvoice, setEditableInvoice] = useState({
    labor_rate: 100,
    tax_rate: 8,
    labor_hours: 0,
    discount: 0,
    discount_type: 'amount', // 'amount' or 'percentage'
    status: 'pending',
    notes: '',
    vin: '',
    license_plate: '',
    odometer_in: '',
    work_description: '',
    return_parts: '',
    deposit_type: 'none', // 'none' | 'parts' | 'percentage'
    deposit_percent: ''
  });
  const [editableParts, setEditableParts] = useState([]);
  const [calculatedTotals, setCalculatedTotals] = useState({
    parts_cost: 0,
    labor_cost: 0,
    subtotal: 0,
    discount_amount: 0,
    tax_amount: 0,
    total: 0
  });
  const [sendingInvoice, setSendingInvoice] = useState(false);
  // #52: overlay-scoped loading, deliberately NOT the orchestrator's shared
  // `isLoading`. That flag is passed to DataTable as `loading`, so flipping it
  // swaps the table out for a loading state and unmounts the row button that
  // opened the overlay. Modal.js then has no live node to restore focus to on
  // close, and a keyboard user is dropped back to <body>. Anything that runs
  // while an overlay is open must use this instead.
  const [overlayLoading, setOverlayLoading] = useState(false);

  const openInvoiceModal = async (job) => {
    try {
      console.log('Opening invoice modal for job:', job.id);
      setOverlayLoading(true);
      const invoice = await jobAPI.getJobInvoice(job.id);
      console.log('Invoice data received:', invoice);
      setInvoiceData(invoice);
      setSelectedJobForActions(job);

      // A saved invoice (it has a number) keeps its own labor and tax rates; a
      // new one starts from the shop's default preferences.
      const saved = Boolean(invoice.invoice_number);
      setEditableInvoice({
        labor_rate: saved ? invoice.labor_rate : userPreferences.default_labor_rate || 100,
        // `??`, not `||`: a shop that charges no tax has a real rate of 0.
        tax_rate: (saved ? invoice.tax_rate : userPreferences.default_tax_rate ?? 0.08) * 100, // Convert to percentage for display
        labor_hours: invoice.labor_hours || 0,
        discount: invoice.discount || 0,
        discount_type: invoice.discount_type || 'amount',
        status: invoice.status || 'pending',
        notes: invoice.notes || userPreferences.invoice_notes || '',
        vin: invoice.vin || '',
        license_plate: invoice.license_plate || '',
        odometer_in: invoice.odometer_in ?? '',
        work_description: invoice.work_description || '',
        // Tri-state: '' is "not asked", which the PDF prints as two empty boxes.
        return_parts: invoice.return_parts === true ? 'yes' : invoice.return_parts === false ? 'no' : '',
        deposit_type: invoice.deposit_type || 'none',
        deposit_percent: invoice.deposit_percent ?? ''
      });

      // Initialize editable parts from invoice data
      const initialParts = (invoice.parts || []).map((part, index) => ({
        id: index,
        part_name: part.part_name || part.description || '',
        part_number: part.part_number || '',
        quantity: part.quantity || 1,
        unit_cost: part.estimated_cost || part.unit_cost || 0,
        // A saved line with no condition predates conditions (migration 053):
        // keep it unrecorded rather than claim "new". Draft lines default new.
        condition: part.condition || (saved ? '' : 'new')
      }));
      setEditableParts(initialParts);

      setShowInvoiceModal(true);
      console.log('Invoice modal should be open now');
    } catch (error) {
      console.error('Error loading invoice data:', error);
      toast.error(`Error loading invoice: ${error.message || 'Unknown error'}`);
    } finally {
      setOverlayLoading(false);
    }
  };

  // Recalculate totals when editable invoice fields or parts change
  useEffect(() => {
    if (invoiceData || editableParts.length > 0) {
      // Calculate parts cost from editable parts
      let partsCost = editableParts.reduce((sum, part) => {
        return sum + lineTotal(part.quantity, part.unit_cost);
      }, 0);

      partsCost = Math.round(partsCost * 100) / 100;
      const laborCost = editableInvoice.labor_hours * editableInvoice.labor_rate;
      const subtotalBeforeDiscount = partsCost + laborCost;

      // Calculate discount amount
      let discountAmount = 0;
      if (editableInvoice.discount_type === 'percentage') {
        discountAmount = subtotalBeforeDiscount * (editableInvoice.discount / 100);
      } else {
        discountAmount = editableInvoice.discount;
      }

      const subtotal = subtotalBeforeDiscount - discountAmount;
      const taxRate = editableInvoice.tax_rate / 100; // Convert from percentage
      const taxAmount = subtotal * taxRate;
      const total = subtotal + taxAmount;

      setCalculatedTotals({
        parts_cost: partsCost,
        labor_cost: laborCost,
        subtotal_before_discount: subtotalBeforeDiscount,
        discount_amount: discountAmount,
        subtotal: subtotal,
        tax_amount: taxAmount,
        total: total
      });
    }
  }, [invoiceData, editableInvoice, editableParts]);

  // Parts management functions
  const addPart = () => {
    setEditableParts(prev => [...prev, {
      id: Date.now(),
      part_name: '',
      part_number: '',
      quantity: 1,
      unit_cost: 0,
      condition: 'new'
    }]);
  };

  // Appends a canned job to the estimate: its parts as new lines, its hours
  // added to the labour, its work text added to the description. Everything the
  // mechanic already typed is kept. Both updates are functional so two quick
  // adds both land, and pure so StrictMode's double invoke is harmless.
  const applyCannedJob = (cannedJob) => {
    setEditableParts(prev => {
      // Server-opened lines are 0..n-1 and addPart uses Date.now(); one past the
      // largest existing id can collide with neither.
      const base = prev.reduce((max, p) => Math.max(max, Number(p.id) || 0), -1) + 1;
      const added = (cannedJob.parts || []).map((p, i) => ({
        id: base + i,
        part_name: p.part_name,
        part_number: p.part_number ?? '',
        quantity: Number(p.quantity),
        unit_cost: Number(p.unit_cost),
        condition: p.condition || 'new'
      }));
      return [...prev, ...added];
    });
    setEditableInvoice(prev => {
      // Whole cents-of-an-hour in integers: 0.1 + 0.2 must give 0.3.
      const hours = (Number(prev.labor_hours) || 0) + (Number(cannedJob.labor_hours) || 0);
      const jobText = (cannedJob.work_description || '').trim();
      const prevText = prev.work_description || '';
      let work_description = prevText;
      if (jobText) {
        work_description = prevText.trim()
          ? `${prevText.replace(/\s+$/, '')}\n${jobText}`
          : jobText;
      }
      return {
        ...prev,
        labor_hours: Math.round(Number((hours * 100).toPrecision(12))) / 100,
        work_description
      };
    });
  };

  const updatePart = (id, field, value) => {
    setEditableParts(prev => prev.map(part =>
      part.id === id ? { ...part, [field]: value } : part
    ));
  };

  const removePart = (id) => {
    setEditableParts(prev => prev.filter(part => part.id !== id));
  };

  // Send invoice to customer email
  const sendInvoiceToCustomer = async () => {
    try {
      if (!selectedJobForActions?.customer_email) {
        toast.error('No customer email on this job. Add one to send the invoice.');
        return;
      }

      setSendingInvoice(true);

      // Build invoice data for sending
      const invoiceToSend = {
        ...invoiceData,
        parts: partsPayload(editableParts),
        labor_rate: editableInvoice.labor_rate,
        tax_rate: editableInvoice.tax_rate / 100,
        labor_hours: editableInvoice.labor_hours,
        discount: editableInvoice.discount,
        discount_type: editableInvoice.discount_type,
        parts_cost: calculatedTotals.parts_cost,
        labor_cost: calculatedTotals.labor_cost,
        subtotal: calculatedTotals.subtotal,
        discount_amount: calculatedTotals.discount_amount,
        tax_amount: calculatedTotals.tax_amount,
        total: calculatedTotals.total,
        notes: editableInvoice.notes,
        business_info: userPreferences
      };

      const result = await jobAPI.sendInvoiceEmail(selectedJobForActions.id, invoiceToSend);
      toast.success(`Invoice sent to ${selectedJobForActions.customer_email}`);

      // Update status to 'sent', but never over 'paid': emailing a paid
      // invoice must not let the next save un-mark it.
      setEditableInvoice(prev => (prev.status === 'paid' ? prev : { ...prev, status: 'sent' }));
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error(`Error sending invoice: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSendingInvoice(false);
    }
  };

  const downloadInvoicePDF = async () => {
    try {
      console.log('Downloading PDF for job:', selectedJobForActions.id);
      setOverlayLoading(true);

      // Build full invoice data for PDF generation
      const pdfData = {
        // Job info
        job_number: invoiceData.job_number,
        customer_name: invoiceData.customer_name,
        vehicle_info: invoiceData.vehicle_info,
        symptoms: invoiceData.symptoms,
        // The saved document's number lets the server add its type, date and
        // approvals; an unsaved one prints as an estimate.
        invoice_number: invoiceData.invoice_number,
        ...documentFields(editableInvoice),
        // Editable parts
        parts: partsPayload(editableParts),
        // Editable invoice values
        labor_hours: editableInvoice.labor_hours,
        labor_rate: editableInvoice.labor_rate,
        labor_cost: calculatedTotals.labor_cost,
        tax_rate: editableInvoice.tax_rate / 100,
        discount: editableInvoice.discount,
        discount_type: editableInvoice.discount_type,
        discount_amount: calculatedTotals.discount_amount,
        parts_cost: calculatedTotals.parts_cost,
        subtotal: calculatedTotals.subtotal,
        tax_amount: calculatedTotals.tax_amount,
        total: calculatedTotals.total,
        status: editableInvoice.status,
        notes: editableInvoice.notes,
        // Business info
        business_name: userPreferences.business_name,
        business_address: userPreferences.business_address,
        business_phone: userPreferences.business_phone
      };

      console.log('Sending PDF data:', JSON.stringify(pdfData, null, 2));
      console.log('Parts being sent:', pdfData.parts);

      const pdfBlob = await jobAPI.generateInvoicePDFWithData(selectedJobForActions.id, pdfData);

      console.log('PDF blob received:', pdfBlob);

      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoiceData.document_type === 'invoice' ? 'invoice' : 'estimate'}_${selectedJobForActions.job_number}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Invoice PDF downloaded');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      // Try to read error message from blob response if possible
      let errorMessage = error.message || 'Unknown error';
      if (error.response?.data) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          errorMessage = parsed.detail || errorMessage;
        } catch (e) {
          // If we can't parse, use the original error
        }
      }
      toast.error(`Error downloading PDF: ${errorMessage}`);
    } finally {
      setOverlayLoading(false);
    }
  };

  // Returns the saved invoice number, or null if the save failed. `quiet`
  // skips the toast for saves made on the way to another action.
  const saveInvoiceToDatabase = async ({ quiet = false } = {}) => {
    try {
      console.log('Saving invoice for job:', selectedJobForActions.id);
      setOverlayLoading(true);

      // Build invoice data from editable state and calculated totals
      const invoiceToSave = {
        ...invoiceData,
        ...documentFields(editableInvoice),
        ...depositFields(editableInvoice),
        parts: partsPayload(editableParts),
        labor_rate: editableInvoice.labor_rate,
        tax_rate: editableInvoice.tax_rate / 100, // Convert to decimal
        labor_hours: editableInvoice.labor_hours,
        discount: editableInvoice.discount,
        discount_type: editableInvoice.discount_type,
        parts_cost: calculatedTotals.parts_cost,
        labor_cost: calculatedTotals.labor_cost,
        subtotal: calculatedTotals.subtotal,
        discount_amount: calculatedTotals.discount_amount,
        tax_amount: calculatedTotals.tax_amount,
        total: calculatedTotals.total,
        status: editableInvoice.status,
        // The status the form loaded with: the server only treats a CHANGE the
        // shop made as marking (or un-marking) the invoice paid.
        loaded_status: invoiceData?.status ?? null,
        notes: editableInvoice.notes,
        business_name: userPreferences.business_name,
        business_address: userPreferences.business_address,
        business_phone: userPreferences.business_phone
      };

      console.log('Invoice data to save:', invoiceToSave);

      const result = await jobAPI.saveJobInvoice(selectedJobForActions.id, invoiceToSave);
      console.log('Save result:', result);
      if (!quiet) toast.success(`Invoice ${result.invoice_number} saved`);
      // The next save edits this invoice rather than creating another one, and
      // starts from the status as stored (the payments may have corrected it).
      setInvoiceData((current) => ({
        ...current,
        invoice_number: result.invoice_number,
        ...(result.status ? { status: result.status } : {}),
      }));
      if (result.status) setEditableInvoice((prev) => ({ ...prev, status: result.status }));

      // Reload documents if on that tab
      onInvoiceSaved();
      return result.invoice_number;
    } catch (error) {
      console.error('Error saving invoice:', error);
      // detail is a string for our own 4xx/5xx messages (the deposit 409, the
      // Stripe 502) but an array for validation errors; messageFromError copes with both.
      toast.error(`Error saving invoice: ${messageFromError(error, error.message || 'Unknown error')}`);
      return null;
    } finally {
      setOverlayLoading(false);
    }
  };

  // Both save first: an approval snapshots the SAVED totals on the server, so
  // recording one against unsaved edits would record the wrong price.
  const recordApproval = async (form) => {
    const number = await saveInvoiceToDatabase({ quiet: true });
    if (!number) return false;
    try {
      const approval = await jobAPI.recordInvoiceAuthorization(selectedJobForActions.id, form);
      setInvoiceData((current) => ({
        ...current,
        authorizations: [...(current.authorizations || []), approval],
      }));
      toast.success('Approval recorded');
      return true;
    } catch (error) {
      toast.error(`Could not record the approval: ${error.response?.data?.detail || error.message}`);
      return false;
    }
  };

  // After a payment is recorded the server may have marked the invoice paid.
  // Take only the status from a fresh read, so nothing the mechanic is
  // editing is overwritten, and let the lists refresh.
  const refreshInvoiceStatus = async () => {
    try {
      const fresh = await jobAPI.getJobInvoice(selectedJobForActions.id);
      if (fresh?.status) {
        setEditableInvoice((prev) => ({ ...prev, status: fresh.status }));
        setInvoiceData((current) => ({ ...current, status: fresh.status }));
      }
    } catch (error) {
      console.error('Error refreshing invoice status:', error);
    }
    onInvoiceSaved();
  };

  const setShareLink = (shareLink) =>
    setInvoiceData((current) => ({ ...current, share_link: shareLink }));

  const convertToInvoice = async () => {
    const number = await saveInvoiceToDatabase({ quiet: true });
    if (!number) return false;
    try {
      const converted = await jobAPI.convertInvoice(selectedJobForActions.id);
      setInvoiceData((current) => ({ ...current, ...converted }));
      toast.success('Converted to an invoice');
      onInvoiceSaved();
      return true;
    } catch (error) {
      toast.error(`Could not convert: ${error.response?.data?.detail || error.message}`);
      return false;
    }
  };

  return {
    invoiceOverlayLoading: overlayLoading,
    invoiceData,
    editableInvoice,
    setEditableInvoice,
    editableParts,
    setEditableParts,
    calculatedTotals,
    sendingInvoice,
    openInvoiceModal,
    addPart,
    applyCannedJob,
    updatePart,
    removePart,
    sendInvoiceToCustomer,
    downloadInvoicePDF,
    saveInvoiceToDatabase,
    recordApproval,
    setShareLink,
    refreshInvoiceStatus,
    convertToInvoice
  };
};
